import { useTranslation } from 'react-i18next';
import {
  formatExamDate, daysUntilExamDate, registrationState, sortExamsByNextTest, examSchedule, examContact,
} from '../utils/examDates';

const NAVY       = '#0D1B2A';
const AMBER      = '#F5A623';
const WHITE      = '#FFFFFF';
const LIGHT_GRAY = '#F7F7F7';
const FONT_HEAD  = "'Bebas Neue', sans-serif";
const FONT_BODY  = "'DM Sans', sans-serif";

// Same structure/logic in both variants -- only colors differ.
//  dark:  worker dashboard (navy surfaces)
//  light: public union profile (/unions/:slug) -- white cards on #F7F7F7, navy headings,
//         #444-#888 grays, navy table-style header bar, 2px navy outline secondary buttons.
const PALETTES = {
  dark: {
    sectionBorder:   '1px solid rgba(245,166,35,0.22)',
    bodyBg:          'transparent',
    headerCount:     'rgba(255,255,255,0.45)',
    stepLabel:       AMBER,
    title:           WHITE,
    description:     'rgba(255,255,255,0.6)',
    gridBg:          NAVY,
    gridBorder:      '1px solid rgba(255,255,255,0.06)',
    detailLabel:     'rgba(255,255,255,0.4)',
    detailValue:     WHITE,
    detailMuted:     'rgba(255,255,255,0.5)',
    closedTag:       { color: '#F09595', bg: 'rgba(226,75,74,0.15)' },
    guideBtn:        { color: AMBER, border: '1px solid rgba(245,166,35,0.45)' },
    rail:            'rgba(245,166,35,0.25)',
    divider:         '1px dashed rgba(255,255,255,0.12)',
    listLabel:       'rgba(255,255,255,0.45)',
    plainLabel:      'rgba(255,255,255,0.4)',
    listText:        'rgba(255,255,255,0.8)',
    fontScale:       1,
  },
  light: {
    sectionBorder:   '1px solid rgba(13,27,42,0.12)',
    bodyBg:          WHITE,
    headerCount:     'rgba(255,255,255,0.6)',
    // Plain #F5A623 is ~2:1 on white -- fine for the page's large rate text, too faint for
    // 10px caps, so small amber labels use a darkened amber that clears 4.5:1.
    stepLabel:       '#9A6300',
    title:           NAVY,
    description:     '#666',
    gridBg:          LIGHT_GRAY,
    gridBorder:      '1px solid rgba(13,27,42,0.08)',
    detailLabel:     '#777',
    detailValue:     '#333',
    detailMuted:     '#888',
    closedTag:       { color: '#B42318', bg: 'rgba(220,38,38,0.1)' },
    guideBtn:        { color: NAVY, border: `2px solid ${NAVY}` },
    rail:            'rgba(245,166,35,0.45)',
    divider:         '1px dashed rgba(13,27,42,0.15)',
    listLabel:       '#777',
    plainLabel:      '#777',
    listText:        '#444',
    fontScale:       1.08,
  },
};

const px = (n, p) => `${Math.round(n * p.fontScale * 10) / 10}px`;

function RequirementList({ requirements, p }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {requirements.map((r, i) => (
        <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', fontSize: px(13, p), color: p.listText, lineHeight: 1.5 }}>
          <span aria-hidden="true" style={{ width: '13px', height: '13px', marginTop: '3px', flexShrink: 0, border: `1.5px solid ${AMBER}`, borderRadius: '3px' }} />
          <span>{r}</span>
        </li>
      ))}
    </ul>
  );
}

function ExamStep({ exam, number, isLast, p }) {
  const { t } = useTranslation();
  // An exam can run on several dates; a worker only needs the one they can still sign up for,
  // so this is the soonest upcoming session (or the legacy single date on older exams).
  const schedule = examSchedule(exam);
  const regState = schedule.individual ? null : registrationState({ registration_deadline: schedule.registration_deadline });
  const regDays  = daysUntilExamDate(schedule.registration_deadline);
  const testDays = daysUntilExamDate(schedule.test_date);
  const contact  = examContact(exam);

  const details = [
    // No fixed date to give: one plain line replaces both date cells, since a blank "register
    // by" next to it would only read as missing information.
    schedule.individual
      ? { key: 'individual', label: t('exams.worker.scheduleLabel'), value: t('exams.worker.scheduledIndividually') }
      : {
          key: 'test',
          label: t('exams.worker.nextTestDate'),
          value: schedule.test_date
            ? `${formatExamDate(schedule.test_date, { weekday: true })}${testDays !== null && testDays < 0 ? ` ${t('exams.worker.pastSuffix')}` : ''}`
            : t('exams.worker.dateTba'),
          muted: !schedule.test_date || (testDays !== null && testDays < 0),
        },
    !schedule.individual && {
      key: 'deadline',
      label: t('exams.worker.registerBy'),
      value: schedule.registration_deadline ? formatExamDate(schedule.registration_deadline) : t('exams.worker.dateTba'),
      muted: !schedule.registration_deadline,
      tag: regState === 'closed'
        ? { text: t('exams.shared.registrationClosed'), ...p.closedTag }
        : regState === 'closingSoon'
          ? { text: regDays === 0 ? t('exams.shared.closesToday') : t('exams.shared.closesInDays', { count: regDays }), color: NAVY, bg: AMBER }
          : null,
    },
    exam.location      && { key: 'location', label: t('exams.shared.locationLabel'),     value: exam.location },
    exam.passing_score && { key: 'score',    label: t('exams.shared.passingScoreLabel'), value: exam.passing_score },
    exam.exam_fee      && { key: 'fee',      label: t('exams.shared.examFeeLabel'),      value: exam.exam_fee },
  ].filter(Boolean);

  return (
    <li style={{ display: 'flex', gap: '12px' }}>
      {/* Number + connector rail */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <div style={{
          width: '28px', height: '28px', borderRadius: '50%', backgroundColor: AMBER, color: NAVY,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: FONT_HEAD, fontSize: '17px', lineHeight: 1, paddingTop: '2px',
        }}>
          {number}
        </div>
        {!isLast && <div style={{ flex: 1, width: '2px', backgroundColor: p.rail, marginTop: '4px' }} />}
      </div>

      <div style={{ flex: 1, minWidth: 0, paddingBottom: isLast ? 0 : '18px' }}>
        <p style={{ fontSize: '10px', fontWeight: 700, color: p.stepLabel, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '2px' }}>
          {t('exams.worker.stepLabel', { number })}
        </p>
        <h4 style={{ fontSize: px(15, p), fontWeight: 700, color: p.title, lineHeight: 1.3, marginBottom: exam.description ? '4px' : '10px' }}>
          {t('exams.worker.passExam', { name: exam.name })}
        </h4>
        {exam.description && (
          <p style={{ fontSize: px(12.5, p), color: p.description, lineHeight: 1.55, marginBottom: '10px', whiteSpace: 'pre-line' }}>
            {exam.description}
          </p>
        )}

        <dl style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px 16px',
          margin: 0, padding: '10px 12px', backgroundColor: p.gridBg, borderRadius: '8px', border: p.gridBorder,
        }}>
          {details.map(d => (
            <div key={d.key} style={{ minWidth: 0 }}>
              <dt style={{ fontSize: '10px', fontWeight: 700, color: p.detailLabel, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>{d.label}</dt>
              <dd style={{ margin: 0, fontSize: px(13, p), fontWeight: 600, lineHeight: 1.4, overflowWrap: 'anywhere', color: d.muted ? p.detailMuted : p.detailValue }}>{d.value}</dd>
              {d.tag && (
                <span style={{ display: 'inline-block', marginTop: '4px', backgroundColor: d.tag.bg, color: d.tag.color, borderRadius: '999px', fontSize: '10px', fontWeight: 700, padding: '2px 8px' }}>
                  {d.tag.text}
                </span>
              )}
            </div>
          ))}
        </dl>

        {exam.study_guide_url && (
          <a
            href={exam.study_guide_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '10px',
              fontSize: px(12, p), fontWeight: 700, color: p.guideBtn.color, textDecoration: 'none',
              border: p.guideBtn.border, borderRadius: '6px', padding: '6px 11px',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
            </svg>
            {t('exams.worker.studyGuide')}
          </a>
        )}

        {/* Only appears when the union actually named someone. Phone and email are real
            tel:/mailto: links -- on a phone this is the fastest path to a person. */}
        {contact && (
          <p data-exam-contact style={{
            marginTop: '10px', fontSize: px(12, p), lineHeight: 1.55, color: p.description,
            borderLeft: `2px solid ${AMBER}`, paddingLeft: '9px',
          }}>
            <strong style={{ color: p.detailValue, fontWeight: 700 }}>{t('exams.worker.contactQuestions')}</strong>{' '}
            {contact.name
              ? <>{t('exams.worker.contactLead')} <strong style={{ color: p.detailValue, fontWeight: 700 }}>{contact.name}</strong></>
              : t('exams.worker.contactLead')}
            {contact.title && <>, {contact.title}</>}
            {(contact.phone || contact.email) && ' — '}
            {contact.phone && (
              <a href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`} style={{ color: p.guideBtn.color, fontWeight: 700, textDecoration: 'none', overflowWrap: 'anywhere' }}>
                {contact.phone}
              </a>
            )}
            {contact.phone && contact.email && ' / '}
            {contact.email && (
              <a href={`mailto:${contact.email}`} style={{ color: p.guideBtn.color, fontWeight: 700, textDecoration: 'none', overflowWrap: 'anywhere' }}>
                {contact.email}
              </a>
            )}
          </p>
        )}
      </div>
    </li>
  );
}

// "What stands between me and an interview" block for one apprenticeship posting.
// Linked union_exams render as numbered steps; the posting's plain `requirements` array follows.
// With no linked exams this collapses to just the requirements list -- no empty exam section.
export default function ApprenticeshipPrerequisites({ exams = [], requirements = [], variant = 'dark' }) {
  const { t } = useTranslation();
  const p = PALETTES[variant] || PALETTES.dark;
  const reqs = (requirements || []).filter(r => (r || '').trim());
  const sortedExams = sortExamsByNextTest(exams || []);

  if (sortedExams.length === 0) {
    if (reqs.length === 0) return null;
    return (
      <div style={{ fontFamily: FONT_BODY }}>
        <div style={{ fontSize: '11px', color: p.plainLabel, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: variant === 'light' ? 700 : 400 }}>
          {t('worker.dashboard.apprenticeships.requirementsLabel')}
        </div>
        <RequirementList requirements={reqs} p={p} />
      </div>
    );
  }

  return (
    <section style={{ fontFamily: FONT_BODY, border: p.sectionBorder, borderRadius: '10px', overflow: 'hidden' }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap',
        backgroundColor: NAVY, borderBottom: `2px solid ${AMBER}`, padding: '9px 14px 7px',
      }}>
        <h3 style={{ fontFamily: FONT_HEAD, fontSize: '19px', letterSpacing: '0.06em', color: WHITE, lineHeight: 1 }}>
          {t('exams.worker.beforeInterviewTitle')}
        </h3>
        <span style={{ fontSize: '10px', fontWeight: 700, color: p.headerCount, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          {t('exams.worker.examCount', { count: sortedExams.length })}
        </span>
      </div>

      <div style={{ padding: '14px', backgroundColor: p.bodyBg }}>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {sortedExams.map((exam, i) => (
            <ExamStep key={exam.id} exam={exam} number={i + 1} isLast={i === sortedExams.length - 1} p={p} />
          ))}
        </ol>

        {reqs.length > 0 && (
          <div style={{ marginTop: '16px', paddingTop: '14px', borderTop: p.divider }}>
            <p style={{ fontSize: '10px', fontWeight: 700, color: p.listLabel, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>
              {t('exams.worker.alsoRequired')}
            </p>
            <RequirementList requirements={reqs} p={p} />
          </div>
        )}
      </div>
    </section>
  );
}
